import { api } from '../client';
import type { TrackingEventMetadata, ETAMetadata } from '../../types';
import type { Location } from './locations.api';

// ============================================
// Types & Interfaces
// ============================================

// Helper function to extract latitude from location
export function getLocationLat(location: Location): number {
  return location.coordinates?.lat || 0;
}

// Helper function to extract longitude from location
export function getLocationLng(location: Location): number {
  return location.coordinates?.lng || 0;
}

export interface ShipmentPosition {
  id: string;
  shipmentId: string;
  latitude: number;
  longitude: number;
  heading?: number;
  speed?: number;
  altitude?: number;
  source: 'AIS' | 'GPS' | 'ESTIMATED' | 'MANUAL' | 'ELD';
  accuracy?: number;
  timestamp: string;
  updatedAt: string;
}

export interface TrackingEvent {
  id: string;
  shipmentId: string;
  eventType: string;
  eventCategory: 'MILESTONE' | 'EXCEPTION' | 'POSITION_UPDATE' | 'DOCUMENT';
  location?: {
    city?: string;
    state?: string;
    country?: string;
    lat?: number;
    lon?: number;
    locationName?: string;
  };
  timestamp: string;
  isEstimated: boolean;
  description?: string;
  metadata?: TrackingEventMetadata;
  createdAt: string;
}

export interface ShipmentETA {
  id: string;
  shipmentId: string;
  milestoneType: string;
  estimatedArrival: string;
  confidence?: number;
  source: 'CARRIER' | 'ML_PREDICTION' | 'MANUAL' | 'HISTORICAL' | 'TRAFFIC_ADJUSTED';
  calculatedAt: string;
  metadata?: ETAMetadata;
}

export interface TemperatureLog {
  id: string;
  shipmentId: string;
  temperature: number;
  humidity?: number;
  location?: {
    lat?: number;
    lon?: number;
    city?: string;
  };
  timestamp: string;
  isAlert: boolean;
  alertReason?: 'ABOVE_MAX' | 'BELOW_MIN' | 'RAPID_CHANGE' | 'SENSOR_FAILURE';
  createdAt: string;
}

export interface ShipmentTracking {
  id: string;
  shipmentNumber: string;
  trackingNumber?: string;
  companyId: string;
  status: string;
  carrier?: string;
  transportMode?: 'OCEAN' | 'AIR' | 'ROAD_FTL' | 'ROAD_LTL' | 'RAIL' | 'MULTIMODAL';
  currentLocation?: {
    city: string;
    state: string;
    country: string;
    lat: number;
    lon: number;
  };
  lastPositionUpdate?: string;
  estimatedDeparture?: string;
  actualDeparture?: string;
  estimatedArrival?: string;
  actualArrival?: string;
  totalWeight?: number;
  totalVolume?: number;
  origin: Location;
  destination: Location;
  position?: ShipmentPosition;
  trackingEvents?: TrackingEvent[];
  etas?: ShipmentETA[];
  temperatureLogs?: TemperatureLog[];
  // Demurrage & Detention fields
  freeTimeExpiry?: Date | string;
  totalDemurrageCharges?: number;
  demurrageStatus?:
    | 'WITHIN_FREE_TIME'
    | 'FREE_TIME_EXPIRING'
    | 'DEMURRAGE_ACCRUING'
    | 'CONTAINER_RETURNED'
    | 'WAIVED';
  createdAt: string;
  updatedAt: string;
}

export interface TrackingFilters {
  companyId?: string;
  status?: string;
  mode?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  skip?: number;
  take?: number;
}

export interface CreateTrackingEventPayload {
  eventType: string;
  eventCategory: 'MILESTONE' | 'EXCEPTION' | 'POSITION_UPDATE' | 'DOCUMENT';
  location?: {
    city?: string;
    state?: string;
    country?: string;
    lat?: number;
    lon?: number;
    locationName?: string;
  };
  timestamp: string;
  isEstimated?: boolean;
  description?: string;
  metadata?: TrackingEventMetadata;
}

export interface UpdatePositionPayload {
  latitude: number;
  longitude: number;
  heading?: number;
  speed?: number;
  altitude?: number;
  source: 'AIS' | 'GPS' | 'ESTIMATED' | 'MANUAL' | 'ELD';
  accuracy?: number;
  timestamp: string;
}

// Document Management Types
export enum ShipmentDocType {
  BILL_OF_LADING = 'BILL_OF_LADING',
  PROOF_OF_DELIVERY = 'PROOF_OF_DELIVERY',
  COMMERCIAL_INVOICE = 'COMMERCIAL_INVOICE',
  PACKING_LIST = 'PACKING_LIST',
  CUSTOMS_DECLARATION = 'CUSTOMS_DECLARATION',
  INSURANCE_CERTIFICATE = 'INSURANCE_CERTIFICATE',
  CARRIER_BOOKING = 'CARRIER_BOOKING',
  DELIVERY_RECEIPT = 'DELIVERY_RECEIPT',
  PHOTO_DAMAGE_REPORT = 'PHOTO_DAMAGE_REPORT',
  OTHER = 'OTHER',
}

export interface ShipmentDocument {
  id: string;
  shipmentId: string;
  documentType: ShipmentDocType;
  documentName: string;
  documentUrl: string;
  documentSize: number;
  mimeType: string;
  uploadedById: string;
  uploadedBy: {
    id: string;
    fullName: string;
    email: string;
  };
  uploadedAt: Date;
  notes: string | null;
}

export interface UploadDocumentPayload {
  file: File;
  documentType: ShipmentDocType;
  notes?: string;
}

// ============================================
// API Service
// ============================================

export const trackingApi = {
  /**
   * Get all active shipments with tracking data
   */
  async getActiveShipments(params: TrackingFilters) {
    const response = await api.get<{
      data: ShipmentTracking[];
      total: number;
      skip: number;
      take: number;
    }>('/tms/tracking', { params });
    return response.data;
  },

  /**
   * Get full tracking data for a single shipment
   */
  async getShipmentTracking(shipmentId: string) {
    const response = await api.get<ShipmentTracking>(`/tms/tracking/${shipmentId}`);
    return response.data;
  },

  /**
   * Get tracking timeline for a shipment
   */
  async getTimeline(shipmentId: string) {
    const response = await api.get<TrackingEvent[]>(`/tms/tracking/${shipmentId}/timeline`);
    return response.data;
  },

  /**
   * Get temperature history for reefer shipments
   */
  async getTemperature(shipmentId: string, hours: number = 24) {
    const response = await api.get<TemperatureLog[]>(`/tms/tracking/${shipmentId}/temperature`, {
      params: { hours },
    });
    return response.data;
  },

  /**
   * Add a tracking event
   */
  async addEvent(shipmentId: string, payload: CreateTrackingEventPayload) {
    const response = await api.post<TrackingEvent>(`/tms/tracking/${shipmentId}/events`, payload);
    return response.data;
  },

  /**
   * Update shipment position
   */
  async updatePosition(shipmentId: string, payload: UpdatePositionPayload) {
    const response = await api.patch<ShipmentPosition>(
      `/tms/tracking/${shipmentId}/position`,
      payload
    );
    return response.data;
  },

  /**
   * Seed mock tracking data (Development only)
   */
  async seedMockData(companyId: string) {
    const response = await api.post('/tms/tracking/seed-mock-data', {
      companyId,
    });
    return response.data;
  },

  // ============================================
  // Document Management Methods
  // ============================================

  documents: {
    /**
     * Upload a document for a shipment
     */
    async uploadDocument(
      shipmentId: string,
      payload: UploadDocumentPayload
    ): Promise<ShipmentDocument> {
      const formData = new FormData();
      formData.append('file', payload.file);
      formData.append('documentType', payload.documentType);
      if (payload.notes) {
        formData.append('notes', payload.notes);
      }

      const response = await api.post<ShipmentDocument>(
        `/tms/tracking/${shipmentId}/documents`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000, // 60 second timeout for large files
        }
      );

      // Convert uploadedAt to Date object
      return {
        ...response.data,
        uploadedAt: new Date(response.data.uploadedAt),
      };
    },

    /**
     * Get all documents for a shipment
     */
    async getDocuments(shipmentId: string): Promise<ShipmentDocument[]> {
      const response = await api.get<{ documents: ShipmentDocument[]; total: number }>(
        `/tms/tracking/${shipmentId}/documents`
      );

      // Convert upload dates to Date objects
      return response.data.documents.map((doc) => ({
        ...doc,
        uploadedAt: new Date(doc.uploadedAt),
      }));
    },

    /**
     * Get documents by type for a shipment
     */
    async getDocumentsByType(
      shipmentId: string,
      type: ShipmentDocType
    ): Promise<ShipmentDocument[]> {
      const response = await api.get<ShipmentDocument[]>(
        `/tms/tracking/${shipmentId}/documents/type/${type}`
      );

      // Convert upload dates to Date objects
      return response.data.map((doc) => ({
        ...doc,
        uploadedAt: new Date(doc.uploadedAt),
      }));
    },

    /**
     * Download a document
     */
    async downloadDocument(shipmentId: string, docId: string): Promise<Blob> {
      const response = await api.get(`/tms/tracking/${shipmentId}/documents/${docId}/download`, {
        responseType: 'blob',
      });
      return response.data;
    },

    /**
     * Delete a document
     */
    async deleteDocument(shipmentId: string, docId: string): Promise<void> {
      await api.delete(`/tms/tracking/${shipmentId}/documents/${docId}`);
    },
  },
};
